import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Search,
  Loader2,
  Mail,
  Phone,
  Linkedin,
  FileSpreadsheet,
  FileText,
  Sparkles,
  ChevronLeft,
  ChevronRight,
  Wand2,
  ShieldCheck,
  AlertTriangle,
  Building2,
  MapPin,
  History,
  ListChecks,
  Trash2,
  BookmarkPlus,
  PenSquare,
  RefreshCw,
  Clock,
  Tag,
} from "lucide-react";
import { toast } from "sonner";
import { api, API_BASE, errMsg, getToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Textarea } from "@/components/ui/textarea";
import { SectionCard, Segmented } from "@/components/common/Cards";
import { Field } from "@/components/common/Inputs";
import { fmtDate } from "@/lib/format";

const INITIAL_FILTERS = {
  provider: "apollo",
  title: "",
  company: "",
  industry: "",
  location: "",
  keywords: "",
  page: 1,
  per_page: 25,
};

const STATUS_META = {
  new: { label: "Baru", tone: "border-primary/50 bg-[hsl(43_28%_9%)] text-primary" },
  contacted: { label: "Dihubungi", tone: "border-sky-500/50 bg-sky-950/40 text-sky-300" },
  replied: { label: "Balas", tone: "border-amber-500/50 bg-amber-950/40 text-amber-200" },
  deal: { label: "Deal", tone: "border-emerald-500/50 bg-emerald-950/40 text-emerald-300" },
  lost: { label: "Lost", tone: "border-red-500/50 bg-red-950/40 text-red-300" },
};

const STATUS_ORDER = ["new", "contacted", "replied", "deal", "lost"];

function keyOf(row, fallbackIdx) {
  return row.id || row.linkedin_url || `${row.name}-${row.email || row.phone || fallbackIdx}`;
}

function summarizeFilters(filters) {
  const chips = [];
  if (filters.title) chips.push(["Jabatan", filters.title]);
  if (filters.company) chips.push(["Perusahaan", filters.company]);
  if (filters.industry) chips.push(["Industri", filters.industry]);
  if (filters.location) chips.push(["Wilayah", filters.location]);
  if (filters.keywords) chips.push(["Kata kunci", filters.keywords]);
  return chips;
}

export default function ScraperToolPage() {
  const [activeTab, setActiveTab] = useState("search");
  // --- search ---
  const [form, setForm] = useState(INITIAL_FILTERS);
  const [loading, setLoading] = useState(false);
  const [revealingId, setRevealingId] = useState(null);
  const [results, setResults] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, per_page: 25, total_entries: 0 });
  const [error, setError] = useState("");
  const [selected, setSelected] = useState({});
  const [savingLeads, setSavingLeads] = useState(false);
  // --- history ---
  const [historyItems, setHistoryItems] = useState(null);
  const [historyLoadingId, setHistoryLoadingId] = useState(null);
  const [clearHistoryOpen, setClearHistoryOpen] = useState(false);
  // --- leads ---
  const [leadsState, setLeadsState] = useState({ items: [], counts: { total: 0 }, total: 0, page: 1, per_page: 50 });
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [leadsFilter, setLeadsFilter] = useState({ status: "", q: "", page: 1, per_page: 50 });
  const [leadsSelected, setLeadsSelected] = useState({});
  const [editingLead, setEditingLead] = useState(null);
  const [deleteLead, setDeleteLead] = useState(null);

  const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const hasAnyFilter = !!(form.title || form.company || form.industry || form.location || form.keywords);

  const summary = useMemo(() => {
    const totalEmail = results.filter((r) => r.email_available).length;
    const totalPhone = results.filter((r) => r.phone_available).length;
    return { totalEmail, totalPhone };
  }, [results]);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get("/scraper/history");
      setHistoryItems(data.items || []);
    } catch (e) {
      toast.error(errMsg(e, "Gagal memuat riwayat"));
      setHistoryItems([]);
    }
  }, []);

  const loadLeads = useCallback(async () => {
    setLeadsLoading(true);
    try {
      const { data } = await api.get("/scraper/leads", {
        params: {
          status: leadsFilter.status || undefined,
          q: leadsFilter.q || undefined,
          page: leadsFilter.page,
          per_page: leadsFilter.per_page,
        },
      });
      setLeadsState({
        items: data.items || [],
        counts: data.counts || { total: 0 },
        total: data.total || 0,
        page: data.page || 1,
        per_page: data.per_page || 50,
      });
    } catch (e) {
      toast.error(errMsg(e, "Gagal memuat daftar lead"));
    } finally {
      setLeadsLoading(false);
    }
  }, [leadsFilter]);

  useEffect(() => {
    if (activeTab === "history" && historyItems === null) {
      loadHistory();
    }
    if (activeTab === "leads") {
      loadLeads();
    }
  }, [activeTab, historyItems, loadHistory, loadLeads]);

  const runSearch = async (nextPage = 1) => {
    if (!hasAnyFilter) {
      setError("Isi minimal satu filter (Jabatan, Perusahaan, Industri, Wilayah, atau Keyword).");
      return;
    }
    setError("");
    setLoading(true);
    setSelected({});
    try {
      const body = {
        provider: form.provider,
        filters: {
          title: form.title.trim(),
          company: form.company.trim(),
          industry: form.industry.trim(),
          location: form.location.trim(),
          keywords: form.keywords.trim(),
        },
        page: nextPage,
        per_page: form.per_page,
      };
      const { data } = await api.post("/scraper/search", body);
      setResults(data.results || []);
      setPagination(data.pagination || { page: nextPage, per_page: form.per_page, total_entries: 0 });
      update({ page: nextPage });
      setHistoryItems(null); // invalidate so next tab visit refetches
      if ((data.results || []).length === 0) {
        toast.info("Pencarian berhasil tapi tidak ada kontak yang cocok. Coba ubah filter.");
      }
    } catch (e) {
      const msg = errMsg(e, "Gagal menjalankan pencarian");
      setError(msg);
      toast.error(msg);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const openHistoryItem = async (id) => {
    setHistoryLoadingId(id);
    try {
      const { data } = await api.get(`/scraper/history/${id}`);
      setResults(data.results || []);
      setPagination(data.pagination || { page: 1, per_page: data.per_page || 25, total_entries: (data.results || []).length });
      setForm({
        provider: data.provider || "apollo",
        title: data.filters?.title || "",
        company: data.filters?.company || "",
        industry: data.filters?.industry || "",
        location: data.filters?.location || "",
        keywords: data.filters?.keywords || "",
        page: data.page || 1,
        per_page: data.per_page || 25,
      });
      setSelected({});
      setActiveTab("search");
      toast.success("Riwayat pencarian dimuat.");
    } catch (e) {
      toast.error(errMsg(e, "Gagal memuat riwayat"));
    } finally {
      setHistoryLoadingId(null);
    }
  };

  const deleteHistoryItem = async (id) => {
    try {
      await api.delete(`/scraper/history/${id}`);
      setHistoryItems((prev) => (prev || []).filter((h) => h.id !== id));
      toast.success("Riwayat dihapus.");
    } catch (e) {
      toast.error(errMsg(e, "Gagal menghapus riwayat"));
    }
  };

  const clearHistory = async () => {
    try {
      const { data } = await api.delete("/scraper/history");
      setHistoryItems([]);
      toast.success(`Riwayat dibersihkan (${data.deleted || 0}).`);
    } catch (e) {
      toast.error(errMsg(e, "Gagal membersihkan riwayat"));
    } finally {
      setClearHistoryOpen(false);
    }
  };

  const reveal = async (row) => {
    if (form.provider !== "apollo") {
      toast.info("Reveal hanya tersedia untuk provider Apollo.");
      return;
    }
    if (!row.id && !row.name) {
      toast.error("Data profil tidak cukup untuk di-reveal.");
      return;
    }
    setRevealingId(row.id || row.linkedin_url);
    try {
      const { data } = await api.post("/scraper/match", {
        person_id: row.id || null,
        name: row.name || null,
        reveal_phone: true,
      });
      setResults((prev) =>
        prev.map((r) =>
          (r.id && r.id === row.id) || r.linkedin_url === row.linkedin_url
            ? {
                ...r,
                email: data.email || r.email,
                email_available: !!data.email || r.email_available,
                email_locked: data.email_locked ?? r.email_locked,
                phone: data.phone || r.phone,
                phone_available: !!data.phone || r.phone_available,
                email_status: data.email_status || r.email_status,
              }
            : r
        )
      );
      if (data.email || data.phone) {
        toast.success("Kontak berhasil di-reveal.");
      } else {
        toast.info("Apollo tidak menemukan kontak tambahan untuk profil ini.");
      }
    } catch (e) {
      toast.error(errMsg(e, "Gagal reveal kontak"));
    } finally {
      setRevealingId(null);
    }
  };

  const toggleSelect = (key) => setSelected((prev) => ({ ...prev, [key]: !prev[key] }));
  const toggleSelectAll = () => {
    const allSelected = results.length > 0 && results.every((r, i) => selected[keyOf(r, i)]);
    if (allSelected) {
      setSelected({});
    } else {
      const next = {};
      results.forEach((r, i) => {
        next[keyOf(r, i)] = true;
      });
      setSelected(next);
    }
  };
  const selectedCount = Object.values(selected).filter(Boolean).length;

  const saveSelectedAsLeads = async () => {
    const rows = results.filter((r, i) => selected[keyOf(r, i)]);
    if (rows.length === 0) {
      toast.info("Pilih minimal satu kontak dulu.");
      return;
    }
    setSavingLeads(true);
    try {
      const body = {
        leads: rows.map((r) => ({
          name: r.name || "",
          title: r.title || "",
          company: r.company || "",
          industry: r.industry || "",
          location: r.location || "",
          linkedin_url: r.linkedin_url || "",
          email: r.email || null,
          phone: r.phone || null,
          provider: r.provider || form.provider,
        })),
        default_status: "new",
      };
      const { data } = await api.post("/scraper/leads", body);
      toast.success(
        `Lead disimpan · ditambah ${data.added}, diperbarui ${data.updated}${data.skipped ? ", dilewati " + data.skipped : ""}.`
      );
      setSelected({});
      // refresh lead cache lazily
      if (activeTab === "leads") loadLeads();
    } catch (e) {
      toast.error(errMsg(e, "Gagal menyimpan lead"));
    } finally {
      setSavingLeads(false);
    }
  };

  const download = async (format, rowsOverride, filenameHint, includeLeadFields = false) => {
    const rows = rowsOverride || results;
    if (!rows.length) {
      toast.error("Belum ada baris untuk diunduh.");
      return;
    }
    const payloadRows = rows.map((r) => ({
      name: r.name || "",
      title: r.title || "",
      company: r.company || "",
      industry: r.industry || "",
      location: r.location || "",
      linkedin_url: r.linkedin_url || "",
      email: r.email || null,
      email_available: !!(r.email_available ?? !!r.email),
      phone: r.phone || null,
      phone_available: !!(r.phone_available ?? !!r.phone),
      provider: r.provider || form.provider,
      status: r.status || null,
      notes: r.notes || null,
    }));
    try {
      const resp = await fetch(`${API_BASE}/scraper/export`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({
          format,
          rows: payloadRows,
          include_lead_fields: includeLeadFields,
          filename_hint:
            filenameHint ||
            [form.title, form.company, form.location].filter(Boolean).join("-") ||
            "kontak-linkedin",
        }),
      });
      if (!resp.ok) {
        const t = await resp.text();
        throw new Error(t || `HTTP ${resp.status}`);
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const disposition = resp.headers.get("Content-Disposition") || "";
      const match = /filename="?([^"]+)"?/i.exec(disposition);
      a.download = match ? match[1] : `kontak.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(errMsg(e, "Gagal mengunduh file"));
    }
  };

  // --- Lead actions ---
  const changeLeadStatus = async (lead, status) => {
    try {
      const { data } = await api.put(`/scraper/leads/${lead.id}`, { status });
      setLeadsState((prev) => ({
        ...prev,
        items: prev.items.map((l) => (l.id === lead.id ? data : l)),
        counts: {
          ...prev.counts,
          [lead.status]: Math.max(0, (prev.counts[lead.status] || 0) - 1),
          [status]: (prev.counts[status] || 0) + 1,
        },
      }));
      toast.success(`Status diubah ke ${STATUS_META[status].label}.`);
    } catch (e) {
      toast.error(errMsg(e, "Gagal ubah status"));
    }
  };

  const saveLeadEdit = async (patch) => {
    if (!editingLead) return;
    try {
      const { data } = await api.put(`/scraper/leads/${editingLead.id}`, patch);
      setLeadsState((prev) => ({ ...prev, items: prev.items.map((l) => (l.id === editingLead.id ? data : l)) }));
      setEditingLead(null);
      toast.success("Lead diperbarui.");
    } catch (e) {
      toast.error(errMsg(e, "Gagal update lead"));
    }
  };

  const removeLead = async (lead) => {
    try {
      await api.delete(`/scraper/leads/${lead.id}`);
      setLeadsState((prev) => ({
        ...prev,
        items: prev.items.filter((l) => l.id !== lead.id),
        total: Math.max(0, prev.total - 1),
        counts: {
          ...prev.counts,
          total: Math.max(0, (prev.counts.total || 0) - 1),
          [lead.status]: Math.max(0, (prev.counts[lead.status] || 0) - 1),
        },
      }));
      setDeleteLead(null);
      toast.success("Lead dihapus.");
    } catch (e) {
      toast.error(errMsg(e, "Gagal hapus lead"));
    }
  };

  const leadIdsSelected = Object.keys(leadsSelected).filter((k) => leadsSelected[k]);
  const toggleLeadSelect = (id) => setLeadsSelected((prev) => ({ ...prev, [id]: !prev[id] }));
  const toggleLeadSelectAll = () => {
    const all = leadsState.items.length > 0 && leadsState.items.every((l) => leadsSelected[l.id]);
    if (all) setLeadsSelected({});
    else {
      const next = {};
      leadsState.items.forEach((l) => (next[l.id] = true));
      setLeadsSelected(next);
    }
  };
  const bulkDeleteLeads = async () => {
    if (!leadIdsSelected.length) return;
    try {
      const { data } = await api.post("/scraper/leads/bulk-delete", { ids: leadIdsSelected });
      toast.success(`${data.deleted} lead dihapus.`);
      setLeadsSelected({});
      loadLeads();
    } catch (e) {
      toast.error(errMsg(e, "Gagal hapus lead"));
    }
  };

  const totalPages = Math.max(
    1,
    Math.ceil((pagination.total_entries || results.length) / (pagination.per_page || form.per_page))
  );
  const currentPage = pagination.page || form.page;

  return (
    <div className="max-w-[1400px] mx-auto space-y-6" data-testid="scraper-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="eyebrow flex items-center gap-2">
            <Search className="h-3.5 w-3.5" /> Alat Admin
          </div>
          <h1 className="font-display text-4xl mt-1.5" data-testid="scraper-page-title">
            Scraping Kontak LinkedIn
          </h1>
          <p className="text-sm text-muted-foreground mt-1.5 max-w-3xl leading-relaxed">
            Temukan kontak berdasarkan jabatan, perusahaan/industri, dan wilayah. Simpan ke Daftar Lead dan kelola status follow-up.
          </p>
        </div>
        <Badge variant="outline" className="border-primary/40 text-primary w-fit">
          <ShieldCheck className="h-3 w-3 mr-1" /> Admin only
        </Badge>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList
          className="bg-[hsl(220_12%_8%)] border border-border h-auto p-1 flex-wrap"
          data-testid="scraper-tabs"
        >
          <TabsTrigger
            value="search"
            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2"
            data-testid="scraper-tab-search"
          >
            <Search className="h-4 w-4 mr-1.5" /> Pencarian
          </TabsTrigger>
          <TabsTrigger
            value="history"
            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2"
            data-testid="scraper-tab-history"
          >
            <History className="h-4 w-4 mr-1.5" /> Riwayat
          </TabsTrigger>
          <TabsTrigger
            value="leads"
            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2"
            data-testid="scraper-tab-leads"
          >
            <ListChecks className="h-4 w-4 mr-1.5" /> Daftar Lead
            {leadsState.counts?.total > 0 && (
              <Badge variant="outline" className="ml-2 border-primary/40 text-primary num text-[10px]">
                {leadsState.counts.total}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* ======================== SEARCH TAB ======================== */}
        <TabsContent value="search" className="mt-5 space-y-6">
          <SectionCard
            title="Filter pencarian"
            description="Isi minimal satu filter. Hasil dinormalisasi lintas provider."
            testid="scraper-filter-card"
          >
            <div className="space-y-4">
              <Field label="Sumber data">
                <Segmented
                  value={form.provider}
                  onChange={(v) => update({ provider: v })}
                  testid="scraper-provider"
                  options={[
                    { value: "apollo", label: "Apollo.io (kualitas tinggi)" },
                    { value: "rapidapi", label: "RapidAPI LinkedIn (murah)" },
                  ]}
                />
              </Field>

              <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-3">
                <Field label="Jabatan">
                  <Input
                    value={form.title}
                    onChange={(e) => update({ title: e.target.value })}
                    placeholder="CEO, Direktur IT, VP Sales"
                    className="h-10 bg-[hsl(220_12%_8%)]"
                    data-testid="scraper-filter-title"
                  />
                </Field>
                <Field label="Perusahaan">
                  <Input
                    value={form.company}
                    onChange={(e) => update({ company: e.target.value })}
                    placeholder="Astra, Tokopedia…"
                    className="h-10 bg-[hsl(220_12%_8%)]"
                    data-testid="scraper-filter-company"
                  />
                </Field>
                <Field label="Industri / kategori">
                  <Input
                    value={form.industry}
                    onChange={(e) => update({ industry: e.target.value })}
                    placeholder="SaaS, ISP, Logistik"
                    className="h-10 bg-[hsl(220_12%_8%)]"
                    data-testid="scraper-filter-industry"
                  />
                </Field>
                <Field label="Wilayah">
                  <Input
                    value={form.location}
                    onChange={(e) => update({ location: e.target.value })}
                    placeholder="Jakarta, Indonesia, Asia"
                    className="h-10 bg-[hsl(220_12%_8%)]"
                    data-testid="scraper-filter-location"
                  />
                </Field>
                <Field label="Kata kunci tambahan">
                  <Input
                    value={form.keywords}
                    onChange={(e) => update({ keywords: e.target.value })}
                    placeholder="hosting, data center…"
                    className="h-10 bg-[hsl(220_12%_8%)]"
                    data-testid="scraper-filter-keywords"
                  />
                </Field>
              </div>

              {error && (
                <div
                  role="alert"
                  className="rounded-lg border border-[hsl(0_60%_40%)] bg-[hsl(0_40%_10%)] px-3 py-2 text-sm text-[hsl(0_80%_80%)] flex items-start gap-2"
                  data-testid="scraper-error"
                >
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <div className="flex flex-wrap items-center gap-2">
                <Button
                  onClick={() => runSearch(1)}
                  disabled={loading || !hasAnyFilter}
                  className="press"
                  data-testid="scraper-search-button"
                >
                  {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
                  Cari kontak
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setForm(INITIAL_FILTERS);
                    setResults([]);
                    setSelected({});
                    setPagination({ page: 1, per_page: 25, total_entries: 0 });
                    setError("");
                  }}
                  disabled={loading}
                  data-testid="scraper-reset-button"
                >
                  Reset
                </Button>
                <div className="ml-auto flex items-center gap-2 text-xs text-muted-foreground">
                  <span>Per halaman</span>
                  <select
                    value={form.per_page}
                    onChange={(e) => update({ per_page: Number(e.target.value) })}
                    className="h-8 rounded-md border border-border bg-[hsl(220_12%_8%)] px-2 text-xs"
                    data-testid="scraper-perpage"
                  >
                    {[10, 25, 50, 100].map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </SectionCard>

          <SectionCard
            title={results.length ? `Hasil (${results.length})` : "Hasil pencarian"}
            description={
              results.length
                ? `${summary.totalEmail} punya email · ${summary.totalPhone} punya WA/HP`
                : "Jalankan pencarian atau buka Riwayat untuk melihat hasil."
            }
            testid="scraper-results-card"
            actions={
              results.length ? (
                <>
                  <Button
                    size="sm"
                    onClick={saveSelectedAsLeads}
                    disabled={savingLeads || selectedCount === 0}
                    data-testid="scraper-save-leads"
                    className="press"
                  >
                    {savingLeads ? (
                      <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                    ) : (
                      <BookmarkPlus className="h-4 w-4 mr-1.5" />
                    )}
                    Simpan {selectedCount > 0 ? `(${selectedCount}) ` : ""}ke Daftar Lead
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => download("csv")} data-testid="scraper-export-csv">
                    <FileText className="h-4 w-4 mr-1.5" /> CSV
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => download("xlsx")} data-testid="scraper-export-xlsx">
                    <FileSpreadsheet className="h-4 w-4 mr-1.5" /> Excel
                  </Button>
                </>
              ) : null
            }
          >
            {loading ? (
              <div className="space-y-2" data-testid="scraper-loading">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-14 bg-[hsl(220_10%_10%)]" />
                ))}
              </div>
            ) : results.length === 0 ? (
              <div
                className="rounded-xl border border-border bg-[hsl(220_10%_7%)] p-8 text-center text-sm text-muted-foreground"
                data-testid="scraper-empty"
              >
                <Sparkles className="h-6 w-6 mx-auto mb-2 text-primary/70" />
                Belum ada hasil. Isi filter lalu klik <b className="text-foreground">Cari kontak</b>.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="scraper-results-table">
                  <thead>
                    <tr className="text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                      <th className="pl-2 py-2 w-8">
                        <Checkbox
                          checked={results.length > 0 && results.every((r, i) => selected[keyOf(r, i)])}
                          onCheckedChange={toggleSelectAll}
                          aria-label="Pilih semua"
                          data-testid="scraper-select-all"
                        />
                      </th>
                      <th className="text-left py-2">Nama & jabatan</th>
                      <th className="text-left py-2">Perusahaan</th>
                      <th className="text-left py-2">Lokasi</th>
                      <th className="text-left py-2">Kontak</th>
                      <th className="text-right py-2 pr-2">Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((r, idx) => {
                      const rowKey = keyOf(r, idx);
                      const isRevealing = revealingId === (r.id || r.linkedin_url);
                      const hasContact = r.email_available || r.phone_available;
                      return (
                        <tr
                          key={rowKey}
                          className={`border-b border-border hover:bg-[hsl(220_10%_9%)] ${hasContact ? "bg-[hsl(43_28%_8%/0.45)]" : ""}`}
                          data-testid={`scraper-row-${idx}`}
                        >
                          <td className="pl-2 py-3 align-top">
                            <Checkbox
                              checked={!!selected[rowKey]}
                              onCheckedChange={() => toggleSelect(rowKey)}
                              aria-label={`Pilih ${r.name}`}
                              data-testid={`scraper-row-${idx}-check`}
                            />
                          </td>
                          <td className="py-3 align-top">
                            <div className="font-medium text-foreground">{r.name || "-"}</div>
                            <div className="text-xs text-muted-foreground mt-0.5">{r.title || "-"}</div>
                            {r.linkedin_url && (
                              <a
                                href={r.linkedin_url}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1 text-[11px] text-primary mt-1 hover:underline"
                                data-testid={`scraper-row-${idx}-linkedin`}
                              >
                                <Linkedin className="h-3 w-3" /> LinkedIn profile
                              </a>
                            )}
                          </td>
                          <td className="py-3 align-top">
                            <div className="flex items-start gap-2">
                              <Building2 className="h-3.5 w-3.5 mt-0.5 text-muted-foreground shrink-0" />
                              <div>
                                <div className="text-foreground">{r.company || "-"}</div>
                                {r.industry && <div className="text-[11px] text-muted-foreground mt-0.5">{r.industry}</div>}
                              </div>
                            </div>
                          </td>
                          <td className="py-3 align-top">
                            {r.location ? (
                              <span className="inline-flex items-center gap-1 text-foreground">
                                <MapPin className="h-3.5 w-3.5 text-muted-foreground" /> {r.location}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </td>
                          <td className="py-3 align-top">
                            <div className="flex flex-col gap-1.5">
                              {r.email_available ? (
                                <Badge
                                  variant="outline"
                                  className="w-fit border-primary/50 bg-[hsl(43_28%_9%)] text-primary"
                                  data-testid={`scraper-row-${idx}-email-badge`}
                                >
                                  <Mail className="h-3 w-3 mr-1" />
                                  {r.email || (r.email_locked ? "Email terkunci — Reveal" : "Email tersedia")}
                                </Badge>
                              ) : (
                                <span className="text-[11px] text-muted-foreground">Tidak ada email</span>
                              )}
                              {r.phone_available ? (
                                <Badge
                                  variant="outline"
                                  className="w-fit border-emerald-500/50 bg-emerald-950/40 text-emerald-300"
                                  data-testid={`scraper-row-${idx}-phone-badge`}
                                >
                                  <Phone className="h-3 w-3 mr-1" /> {r.phone || "WA/HP tersedia"}
                                </Badge>
                              ) : (
                                <span className="text-[11px] text-muted-foreground">Tidak ada WA/HP</span>
                              )}
                            </div>
                          </td>
                          <td className="py-3 pr-2 align-top text-right">
                            {form.provider === "apollo" && (!r.email || r.email_locked || !r.phone) && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => reveal(r)}
                                disabled={isRevealing}
                                data-testid={`scraper-row-${idx}-reveal`}
                              >
                                {isRevealing ? (
                                  <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                                ) : (
                                  <Wand2 className="h-3.5 w-3.5 mr-1" />
                                )}
                                Reveal
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-4 text-xs text-muted-foreground">
                    <span data-testid="scraper-pagination-info">
                      Halaman {currentPage} dari {totalPages} · total ~{pagination.total_entries || results.length}
                    </span>
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={loading || currentPage <= 1}
                        onClick={() => runSearch(Math.max(1, currentPage - 1))}
                        data-testid="scraper-prev-page"
                      >
                        <ChevronLeft className="h-3.5 w-3.5" /> Sebelumnya
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={loading || currentPage >= totalPages}
                        onClick={() => runSearch(currentPage + 1)}
                        data-testid="scraper-next-page"
                      >
                        Berikutnya <ChevronRight className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </SectionCard>
        </TabsContent>

        {/* ======================== HISTORY TAB ======================== */}
        <TabsContent value="history" className="mt-5">
          <SectionCard
            title="Riwayat pencarian"
            description="Pencarian tersimpan otomatis. Buka kembali untuk melihat hasil tanpa memakai kredit provider."
            testid="scraper-history-card"
            actions={
              <>
                <Button size="sm" variant="outline" onClick={loadHistory} data-testid="scraper-history-refresh">
                  <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
                </Button>
                {(historyItems?.length || 0) > 0 && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-destructive hover:text-destructive"
                    onClick={() => setClearHistoryOpen(true)}
                    data-testid="scraper-history-clear"
                  >
                    <Trash2 className="h-4 w-4 mr-1.5" /> Bersihkan
                  </Button>
                )}
              </>
            }
          >
            {historyItems === null ? (
              <div className="space-y-2">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="h-14 bg-[hsl(220_10%_10%)]" />
                ))}
              </div>
            ) : historyItems.length === 0 ? (
              <div
                className="rounded-xl border border-border bg-[hsl(220_10%_7%)] p-8 text-center text-sm text-muted-foreground"
                data-testid="scraper-history-empty"
              >
                <History className="h-6 w-6 mx-auto mb-2 text-primary/70" />
                Belum ada pencarian tersimpan.
              </div>
            ) : (
              <ul className="space-y-2" data-testid="scraper-history-list">
                {historyItems.map((h) => {
                  const chips = summarizeFilters(h.filters || {});
                  return (
                    <li
                      key={h.id}
                      className="rounded-xl border border-border bg-[hsl(220_10%_7%)] p-4 flex flex-wrap items-center gap-3"
                      data-testid={`scraper-history-item-${h.id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Clock className="h-3.5 w-3.5" /> {fmtDate(h.created_at)}
                          <Badge
                            variant="outline"
                            className="ml-1 border-primary/40 text-primary text-[10px] uppercase"
                          >
                            {h.provider}
                          </Badge>
                          <Badge variant="outline" className="border-border text-[10px] num">
                            {h.result_count} kontak
                          </Badge>
                        </div>
                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                          {chips.length === 0 ? (
                            <span className="text-xs text-muted-foreground">(tanpa filter)</span>
                          ) : (
                            chips.map(([k, v]) => (
                              <Badge
                                key={k}
                                variant="outline"
                                className="border-border text-[11px] text-foreground/90"
                              >
                                <span className="text-muted-foreground mr-1">{k}:</span> {v}
                              </Badge>
                            ))
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          onClick={() => openHistoryItem(h.id)}
                          disabled={historyLoadingId === h.id}
                          data-testid={`scraper-history-open-${h.id}`}
                          className="press"
                        >
                          {historyLoadingId === h.id ? (
                            <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                          ) : (
                            <RefreshCw className="h-4 w-4 mr-1.5" />
                          )}
                          Buka
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-destructive hover:text-destructive"
                          onClick={() => deleteHistoryItem(h.id)}
                          data-testid={`scraper-history-delete-${h.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </SectionCard>
        </TabsContent>

        {/* ======================== LEADS TAB ======================== */}
        <TabsContent value="leads" className="mt-5 space-y-5">
          <SectionCard
            title={`Daftar Lead (${leadsState.counts?.total || 0})`}
            description="Kelola status follow-up kontak yang Anda simpan dari pencarian."
            testid="scraper-leads-card"
            actions={
              <>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => download("csv", leadsState.items, "daftar-lead", true)}
                  disabled={!leadsState.items.length}
                  data-testid="scraper-leads-export-csv"
                >
                  <FileText className="h-4 w-4 mr-1.5" /> CSV
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => download("xlsx", leadsState.items, "daftar-lead", true)}
                  disabled={!leadsState.items.length}
                  data-testid="scraper-leads-export-xlsx"
                >
                  <FileSpreadsheet className="h-4 w-4 mr-1.5" /> Excel
                </Button>
                {leadIdsSelected.length > 0 && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-destructive hover:text-destructive"
                    onClick={bulkDeleteLeads}
                    data-testid="scraper-leads-bulk-delete"
                  >
                    <Trash2 className="h-4 w-4 mr-1.5" /> Hapus {leadIdsSelected.length}
                  </Button>
                )}
              </>
            }
          >
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <div className="flex gap-1 flex-wrap" role="tablist" aria-label="Filter status">
                <button
                  onClick={() => setLeadsFilter((prev) => ({ ...prev, status: "", page: 1 }))}
                  className={`px-3 h-8 rounded-md border text-xs ${leadsFilter.status === "" ? "border-primary bg-[hsl(43_28%_9%)] text-primary" : "border-border bg-[hsl(220_12%_8%)] text-muted-foreground hover:text-foreground"}`}
                  data-testid="scraper-leads-filter-all"
                >
                  Semua <span className="num ml-1">({leadsState.counts?.total || 0})</span>
                </button>
                {STATUS_ORDER.map((s) => (
                  <button
                    key={s}
                    onClick={() => setLeadsFilter((prev) => ({ ...prev, status: s, page: 1 }))}
                    className={`px-3 h-8 rounded-md border text-xs ${leadsFilter.status === s ? "border-primary bg-[hsl(43_28%_9%)] text-primary" : "border-border bg-[hsl(220_12%_8%)] text-muted-foreground hover:text-foreground"}`}
                    data-testid={`scraper-leads-filter-${s}`}
                  >
                    {STATUS_META[s].label}{" "}
                    <span className="num ml-1">({leadsState.counts?.[s] || 0})</span>
                  </button>
                ))}
              </div>
              <div className="ml-auto flex items-center gap-2">
                <Input
                  value={leadsFilter.q}
                  onChange={(e) => setLeadsFilter((prev) => ({ ...prev, q: e.target.value, page: 1 }))}
                  placeholder="Cari nama, perusahaan, email…"
                  className="h-9 w-56 bg-[hsl(220_12%_8%)]"
                  data-testid="scraper-leads-search"
                />
                <Button size="sm" variant="outline" onClick={loadLeads} data-testid="scraper-leads-refresh">
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {leadsLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-14 bg-[hsl(220_10%_10%)]" />
                ))}
              </div>
            ) : leadsState.items.length === 0 ? (
              <div
                className="rounded-xl border border-border bg-[hsl(220_10%_7%)] p-8 text-center text-sm text-muted-foreground"
                data-testid="scraper-leads-empty"
              >
                <ListChecks className="h-6 w-6 mx-auto mb-2 text-primary/70" />
                Belum ada lead. Simpan dari hasil pencarian.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="scraper-leads-table">
                  <thead>
                    <tr className="text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                      <th className="pl-2 py-2 w-8">
                        <Checkbox
                          checked={
                            leadsState.items.length > 0 &&
                            leadsState.items.every((l) => leadsSelected[l.id])
                          }
                          onCheckedChange={toggleLeadSelectAll}
                          aria-label="Pilih semua lead"
                          data-testid="scraper-leads-select-all"
                        />
                      </th>
                      <th className="text-left py-2">Nama & jabatan</th>
                      <th className="text-left py-2">Perusahaan</th>
                      <th className="text-left py-2">Kontak</th>
                      <th className="text-left py-2">Status</th>
                      <th className="text-left py-2">Tag</th>
                      <th className="text-right py-2 pr-2">Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leadsState.items.map((l) => {
                      const meta = STATUS_META[l.status] || STATUS_META.new;
                      return (
                        <tr
                          key={l.id}
                          className="border-b border-border hover:bg-[hsl(220_10%_9%)]"
                          data-testid={`scraper-lead-row-${l.id}`}
                        >
                          <td className="pl-2 py-3 align-top">
                            <Checkbox
                              checked={!!leadsSelected[l.id]}
                              onCheckedChange={() => toggleLeadSelect(l.id)}
                              aria-label={`Pilih ${l.name}`}
                              data-testid={`scraper-lead-row-${l.id}-check`}
                            />
                          </td>
                          <td className="py-3 align-top">
                            <div className="font-medium text-foreground">{l.name || "-"}</div>
                            <div className="text-xs text-muted-foreground mt-0.5">{l.title || "-"}</div>
                            {l.linkedin_url && (
                              <a
                                href={l.linkedin_url}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1 text-[11px] text-primary mt-1 hover:underline"
                              >
                                <Linkedin className="h-3 w-3" /> LinkedIn
                              </a>
                            )}
                          </td>
                          <td className="py-3 align-top">
                            <div className="text-foreground">{l.company || "-"}</div>
                            <div className="text-[11px] text-muted-foreground mt-0.5">
                              {l.industry || ""}
                              {l.industry && l.location ? " · " : ""}
                              {l.location}
                            </div>
                          </td>
                          <td className="py-3 align-top">
                            <div className="flex flex-col gap-1.5">
                              {l.email ? (
                                <Badge
                                  variant="outline"
                                  className="w-fit border-primary/50 bg-[hsl(43_28%_9%)] text-primary"
                                >
                                  <Mail className="h-3 w-3 mr-1" /> {l.email}
                                </Badge>
                              ) : (
                                <span className="text-[11px] text-muted-foreground">no email</span>
                              )}
                              {l.phone ? (
                                <Badge
                                  variant="outline"
                                  className="w-fit border-emerald-500/50 bg-emerald-950/40 text-emerald-300"
                                >
                                  <Phone className="h-3 w-3 mr-1" /> {l.phone}
                                </Badge>
                              ) : (
                                <span className="text-[11px] text-muted-foreground">no wa/hp</span>
                              )}
                            </div>
                          </td>
                          <td className="py-3 align-top">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button
                                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md border text-xs ${meta.tone}`}
                                  data-testid={`scraper-lead-row-${l.id}-status`}
                                >
                                  {meta.label} <ChevronRight className="h-3 w-3 rotate-90" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="start" className="bg-card border-border">
                                {STATUS_ORDER.map((s) => (
                                  <DropdownMenuItem
                                    key={s}
                                    onClick={() => changeLeadStatus(l, s)}
                                    data-testid={`scraper-lead-row-${l.id}-set-${s}`}
                                  >
                                    {STATUS_META[s].label}
                                  </DropdownMenuItem>
                                ))}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                          <td className="py-3 align-top">
                            <div className="flex flex-wrap gap-1 max-w-[180px]">
                              {(l.tags || []).length === 0 ? (
                                <span className="text-[11px] text-muted-foreground">-</span>
                              ) : (
                                (l.tags || []).map((t) => (
                                  <Badge key={t} variant="outline" className="text-[10px] border-border">
                                    <Tag className="h-2.5 w-2.5 mr-1" /> {t}
                                  </Badge>
                                ))
                              )}
                            </div>
                          </td>
                          <td className="py-3 pr-2 align-top text-right">
                            <div className="inline-flex gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setEditingLead(l)}
                                data-testid={`scraper-lead-row-${l.id}-edit`}
                              >
                                <PenSquare className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-destructive hover:text-destructive"
                                onClick={() => setDeleteLead(l)}
                                data-testid={`scraper-lead-row-${l.id}-delete`}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </SectionCard>
        </TabsContent>
      </Tabs>

      {/* Lead edit dialog */}
      <Dialog open={!!editingLead} onOpenChange={(open) => !open && setEditingLead(null)}>
        <DialogContent className="bg-card border-border" data-testid="scraper-lead-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit Lead</DialogTitle>
            <DialogDescription>{editingLead?.name}</DialogDescription>
          </DialogHeader>
          {editingLead && (
            <LeadEditForm
              key={editingLead.id}
              initial={editingLead}
              onCancel={() => setEditingLead(null)}
              onSave={saveLeadEdit}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete lead confirm */}
      <AlertDialog open={!!deleteLead} onOpenChange={(open) => !open && setDeleteLead(null)}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus lead?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteLead?.name} akan dihapus permanen. Tindakan ini tidak dapat dibatalkan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="scraper-lead-delete-cancel">Batal</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteLead && removeLead(deleteLead)}
              data-testid="scraper-lead-delete-confirm"
            >
              Ya, hapus
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Clear history confirm */}
      <AlertDialog open={clearHistoryOpen} onOpenChange={setClearHistoryOpen}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Bersihkan semua riwayat?</AlertDialogTitle>
            <AlertDialogDescription>
              Semua riwayat pencarian beserta hasilnya akan dihapus. Daftar Lead tidak terpengaruh.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="scraper-history-clear-cancel">Batal</AlertDialogCancel>
            <AlertDialogAction onClick={clearHistory} data-testid="scraper-history-clear-confirm">
              Ya, bersihkan
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function LeadEditForm({ initial, onCancel, onSave }) {
  const [status, setStatus] = useState(initial.status || "new");
  const [notes, setNotes] = useState(initial.notes || "");
  const [tags, setTags] = useState((initial.tags || []).join(", "));
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      await onSave({
        status,
        notes,
        tags: tags
          .split(/[,;\n]+/)
          .map((t) => t.trim())
          .filter(Boolean),
      });
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <label className="text-xs text-muted-foreground">Status</label>
        <div className="flex flex-wrap gap-1">
          {STATUS_ORDER.map((s) => {
            const m = STATUS_META[s];
            return (
              <button
                key={s}
                type="button"
                onClick={() => setStatus(s)}
                className={`px-3 h-8 rounded-md border text-xs ${status === s ? m.tone : "border-border bg-[hsl(220_12%_8%)] text-muted-foreground"}`}
                data-testid={`scraper-lead-edit-status-${s}`}
              >
                {m.label}
              </button>
            );
          })}
        </div>
      </div>
      <div className="space-y-2">
        <label htmlFor="lead-notes" className="text-xs text-muted-foreground">Catatan</label>
        <Textarea
          id="lead-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Mis: sudah DM di LinkedIn tgl 10, menunggu respon"
          className="min-h-[90px] bg-[hsl(220_12%_8%)]"
          data-testid="scraper-lead-edit-notes"
        />
      </div>
      <div className="space-y-2">
        <label htmlFor="lead-tags" className="text-xs text-muted-foreground">Tag (pisahkan dengan koma)</label>
        <Input
          id="lead-tags"
          value={tags}
          onChange={(e) => setTags(e.target.value)}
          placeholder="prioritas, event-2026"
          className="h-10 bg-[hsl(220_12%_8%)]"
          data-testid="scraper-lead-edit-tags"
        />
      </div>
      <DialogFooter>
        <Button variant="ghost" onClick={onCancel} data-testid="scraper-lead-edit-cancel">
          Batal
        </Button>
        <Button onClick={submit} disabled={saving} data-testid="scraper-lead-edit-save" className="press">
          {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          Simpan
        </Button>
      </DialogFooter>
    </div>
  );
}
